import { forwardRef } from 'react';
import { clsx } from 'clsx';
import type { ButtonHTMLAttributes } from 'react';
import type { LucideIcon } from 'lucide-react';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger' | 'outline';
  size?: 'sm' | 'md' | 'lg';
  icon?: LucideIcon;
  iconPosition?: 'left' | 'right';
  loading?: boolean;
  loadingText?: string;
  fullWidth?: boolean;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({
    className,
    variant = 'primary',
    size = 'md',
    icon: Icon,
    iconPosition = 'left',
    loading = false,
    loadingText,
    fullWidth = false,
    children,
    disabled,
    ...props
  }, ref) => {
    const baseClasses = 'inline-flex items-center justify-center gap-2 font-semibold rounded-xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed font-plus-jakarta';
    
    const variants = {
      primary: 'bg-primary text-primary-foreground hover:bg-primary/90 focus:ring-primary shadow-material-2 hover:shadow-material-3',
      secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80 focus:ring-secondary shadow-material-1 hover:shadow-material-2',
      outline: 'border border-input bg-background hover:bg-accent hover:text-accent-foreground focus:ring-primary shadow-material-1 hover:shadow-material-2',
      ghost: 'text-foreground hover:bg-accent hover:text-accent-foreground focus:ring-primary',
      danger: 'bg-destructive text-destructive-foreground hover:bg-destructive/90 focus:ring-destructive shadow-material-2 hover:shadow-material-3'
    };
    
    const sizes = {
      sm: 'px-3 py-2 text-sm h-9',
      md: 'px-6 py-2.5 text-sm h-11',
      lg: 'px-8 py-3 text-base h-12'
    };

    return (
      <button
        ref={ref}
        className={clsx(
          baseClasses,
          variants[variant],
          sizes[size],
          fullWidth && 'w-full',
          className
        )}
        disabled={disabled || loading}
        {...props}
      >
        {loading ? (
          <>
            <div className="w-4 h-4 sm:w-5 sm:h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
            <span>{loadingText || 'Loading...'}</span>
          </>
        ) : (
          <>
            {Icon && iconPosition === 'left' && <Icon className="w-4 h-4 sm:w-5 sm:h-5" />}
            {children}
            {Icon && iconPosition === 'right' && <Icon className="w-4 h-4 sm:w-5 sm:h-5" />}
          </>
        )}
      </button>
    );
  }
);

Button.displayName = 'Button';

export { Button };